/**
 * PATH: apps/hr/src/lib/setupApi.ts
 * PURPOSE: Orchestrator — API client setup for HR Hub
 *
 * Convergence C4-MR1: HR previously hand-rolled its own axios instance
 * (own cookie parsing, no token refresh, no 401 handling, no
 * X-Organisation-ID header). It now boots the canonical @quotech/api
 * setupHubApi() like every other hub, so token resolution (auth cookie →
 * Zustand store), the org header, identity-drift detection and the
 * 401 → master-login redirect all come from ONE shared client.
 *
 * BASE-URL CONTRACT: the shared client's baseURL is `${getApiBase()}/api`,
 * so Django paths in ./api/endpoints.ts are written WITHOUT the `/api`
 * prefix (e.g. `/hr/xero-payruns/`). workforce-api calls use absolute
 * URLs built from getApiBase(), which axios sends as-is (baseURL ignored).
 *
 * ERROR CONTRACT: the shared client rejects with a plain ApiError
 * ({ message, status, details }) — not an AxiosError. `message` already
 * carries the server's `detail`/`message` field when present.
 */

import type { AxiosInstance } from 'axios'
import { setupHubApi, getConfiguredClient } from '@quotech/api'
import { getAccessToken, getOrgId, getUserId } from './api/authHelpers'
import { createEndpoints } from './api/endpoints'

/** Call once in main.tsx before rendering. */
export function setupHRApi(): void {
  setupHubApi({ hubName: 'HR' })
}

/**
 * The shared auth-wired axios client. Resolved lazily per call so
 * endpoints always see the client configured by setupHRApi().
 */
export function getApiClient(): AxiosInstance {
  return getConfiguredClient()
}

export const hrApi = createEndpoints(getApiClient)

export { getOrgId, getAccessToken, getUserId }
